import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type {
  PhysicalCardLifecycleAction,
  PhysicalCardLifecycleEntryPoint,
  PhysicalCardLifecycleModalState,
  PhysicalCardLifecyclePhase,
} from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycle.types'
import {
  getPhysicalCardLifecycleAvailability,
  unavailablePhysicalCardLifecycleAvailability,
} from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleAvailability'
import type { PhysicalCardLifecycleErrorKind } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleErrors'
import { classifyPhysicalCardLifecycleError } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleErrors'
import { createPhysicalCardLifecycleIdempotencyKey } from 'src/feature/PhysicalCardLifecycle/physicalCardLifecycleIdempotency'
import type { QueryError } from 'src/queries/network.utils'
import type {
  CapabilityDecisionResponse,
  CardManagementLifecycleOperationResponse,
  PhysicalCardManagementStatus,
} from 'src/queries/physicalCardManagement/physicalCardManagement.models'
import {
  physicalCardManagementMutations,
  physicalCardManagementQueries,
  physicalCardManagementQueryKeys,
  useFreezePhysicalCard,
  useUnfreezePhysicalCard,
} from 'src/queries/physicalCardManagement/physicalCardManagement.queries'
import {
  CREDIT_CARDS_QK,
  creditCardsQueryKeys,
} from 'src/queries/queries.constants'

type LifecycleSelection = {
  action: PhysicalCardLifecycleAction
  entryPoint: PhysicalCardLifecycleEntryPoint
}

type UsePhysicalCardLifecycleParams = {
  creditCardId: string | undefined
  expectedVersion: number | undefined
  cardStatus?: PhysicalCardManagementStatus
  capabilities: CapabilityDecisionResponse[] | undefined
  hasOrgAdminPermissions: boolean
  canDestroyCard: boolean
  onTerminal?: (
    action: PhysicalCardLifecycleAction,
    operation: CardManagementLifecycleOperationResponse,
    modalOpen: boolean,
  ) => void
  onFreezeError?: (errorKind: PhysicalCardLifecycleErrorKind) => void
  onLifecycleError?: (
    action: PhysicalCardLifecycleAction,
    errorKind: PhysicalCardLifecycleErrorKind,
    modalOpen: boolean,
  ) => void
  onTrack?: (
    action: string,
    entryPoint?: PhysicalCardLifecycleEntryPoint,
  ) => void
}

const analyticsPrefix = (action: PhysicalCardLifecycleAction): string => {
  switch (action) {
    case 'lostOrStolen':
      return 'lost_or_stolen'
    case 'reportFraud':
      return 'report_fraud'
    default:
      return action
  }
}

const isTerminalOperation = (
  operation: CardManagementLifecycleOperationResponse | undefined,
): boolean =>
  operation?.status === 'SUCCEEDED' ||
  operation?.status === 'FAILED' ||
  operation?.status === 'CANCELED'

const FREEZE_STATUS_CHANGE_TIMEOUT_MS = 30_000
const LIFECYCLE_OPERATION_TRACKING_TIMEOUT_MS = 2 * 60_000
const LIFECYCLE_APPROVAL_TRACKING_TIMEOUT_MS = 30 * 60_000

export const usePhysicalCardLifecycle = ({
  creditCardId,
  expectedVersion,
  cardStatus,
  capabilities,
  hasOrgAdminPermissions,
  canDestroyCard,
  onTerminal,
  onFreezeError,
  onLifecycleError,
  onTrack,
}: UsePhysicalCardLifecycleParams) => {
  const queryClient = useQueryClient()
  const availability = useMemo(() => {
    if (expectedVersion === undefined) {
      return unavailablePhysicalCardLifecycleAvailability
    }
    return getPhysicalCardLifecycleAvailability(
      capabilities,
      hasOrgAdminPermissions,
      canDestroyCard,
      cardStatus,
    )
  }, [
    canDestroyCard,
    capabilities,
    cardStatus,
    expectedVersion,
    hasOrgAdminPermissions,
  ])
  const replacementMutation = useMutation(
    physicalCardManagementMutations.createCardReplacementRequest(),
  )
  const fraudMutation = useMutation(
    physicalCardManagementMutations.createCardFraudReport(),
  )
  const cancellationMutation = useMutation(
    physicalCardManagementMutations.createCardCancellationRequest(),
  )
  const freezeMutation = useFreezePhysicalCard()
  const unfreezeMutation = useUnfreezePhysicalCard()
  const [selection, setSelection] = useState<LifecycleSelection>()
  const [activeAction, setActiveAction] =
    useState<PhysicalCardLifecycleAction>()
  const [activeEntryPoint, setActiveEntryPoint] =
    useState<PhysicalCardLifecycleEntryPoint>()
  const [operationId, setOperationId] = useState<string>()
  const [submitting, setSubmitting] = useState(false)
  const [errorKind, setErrorKind] = useState<PhysicalCardLifecycleErrorKind>()
  const [isFreezePending, setIsFreezePending] = useState(false)
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    action: 'freeze' | 'unfreeze'
    entryPoint?: PhysicalCardLifecycleEntryPoint
    expectedStatus: 'ACTIVE' | 'FROZEN'
    startingVersion: number
  }>()
  const submittingRef = useRef(false)
  const freezeSubmittingRef = useRef(false)
  const handledTerminalOperationIdRef = useRef<string>()
  const handledOperationReadErrorIdRef = useRef<string>()
  const idempotencyRef = useRef<{
    fingerprint: string
    key: string
  }>()
  const freezeIdempotencyRef = useRef<{
    fingerprint: string
    key: string
  }>()

  const operationQuery = useQuery({
    ...physicalCardManagementQueries.operation(operationId),
    enabled: Boolean(operationId),
  })
  const [acceptedTerminalOperation, setAcceptedTerminalOperation] =
    useState<CardManagementLifecycleOperationResponse>()
  const operation = operationQuery.data ?? acceptedTerminalOperation
  const isModalOpen = selection !== undefined
  const isModalOpenRef = useRef(isModalOpen)
  isModalOpenRef.current = isModalOpen
  const hasUnresolvedLifecycleOperation =
    submitting || Boolean(activeAction && operationId)
  const hasActiveCardOperation =
    hasUnresolvedLifecycleOperation || isFreezePending
  const pendingStatusQuery = useQuery({
    ...physicalCardManagementQueries.status(creditCardId),
    enabled: Boolean(creditCardId && pendingStatusChange),
    refetchInterval: pendingStatusChange ? 3_000 : false,
  })

  useEffect(() => {
    if (
      selection &&
      cardStatus === 'REPLACEMENT_PENDING' &&
      !hasUnresolvedLifecycleOperation &&
      !isFreezePending &&
      !operation
    ) {
      idempotencyRef.current = undefined
      setErrorKind(undefined)
      setSelection(undefined)
      onTrack?.(
        `${analyticsPrefix(selection.action)}_dismissed_replacement_pending`,
        selection.entryPoint,
      )
    }
  }, [
    cardStatus,
    hasUnresolvedLifecycleOperation,
    isFreezePending,
    onTrack,
    operation,
    selection,
  ])

  const refreshAuthoritativeCardState = useCallback((): void => {
    if (!creditCardId) return
    void queryClient.invalidateQueries({
      queryKey: physicalCardManagementQueryKeys.status(creditCardId),
      exact: true,
    })
    void queryClient.invalidateQueries({
      queryKey: physicalCardManagementQueryKeys.capabilities(creditCardId),
      exact: true,
    })
    void queryClient.invalidateQueries({
      queryKey: creditCardsQueryKeys.detail(creditCardId),
      exact: true,
    })
  }, [creditCardId, queryClient])

  useEffect(() => {
    const refreshedStatus = pendingStatusQuery.data
    if (
      !pendingStatusChange ||
      !refreshedStatus ||
      refreshedStatus.version <= pendingStatusChange.startingVersion ||
      refreshedStatus.status !== pendingStatusChange.expectedStatus
    ) {
      return
    }

    freezeSubmittingRef.current = false
    freezeIdempotencyRef.current = undefined
    setPendingStatusChange(undefined)
    setIsFreezePending(false)
    refreshAuthoritativeCardState()
    onTrack?.(
      `${pendingStatusChange.action}_succeeded`,
      pendingStatusChange.entryPoint,
    )
  }, [
    onTrack,
    pendingStatusChange,
    pendingStatusQuery.data,
    refreshAuthoritativeCardState,
  ])

  useEffect(() => {
    if (!pendingStatusChange) return undefined

    const timeoutId = window.setTimeout(() => {
      freezeSubmittingRef.current = false
      freezeIdempotencyRef.current = undefined
      setPendingStatusChange(undefined)
      setIsFreezePending(false)
      refreshAuthoritativeCardState()
      onTrack?.(
        `${pendingStatusChange.action}_status_unconfirmed`,
        pendingStatusChange.entryPoint,
      )
      onFreezeError?.('uncertain')
    }, FREEZE_STATUS_CHANGE_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [
    onFreezeError,
    onTrack,
    pendingStatusChange,
    refreshAuthoritativeCardState,
  ])

  useEffect(() => {
    if (
      !operationId ||
      !activeAction ||
      !operation ||
      isTerminalOperation(operation)
    ) {
      return undefined
    }

    const timeoutMs =
      operation.status === 'PENDING_APPROVAL'
        ? LIFECYCLE_APPROVAL_TRACKING_TIMEOUT_MS
        : LIFECYCLE_OPERATION_TRACKING_TIMEOUT_MS
    const timeoutId = window.setTimeout(() => {
      setErrorKind('uncertain')
      setOperationId(undefined)
      setActiveAction(undefined)
      setActiveEntryPoint(undefined)
      refreshAuthoritativeCardState()
      onTrack?.(
        `${analyticsPrefix(activeAction)}_status_unconfirmed`,
        activeEntryPoint,
      )
      onLifecycleError?.(activeAction, 'uncertain', isModalOpenRef.current)
    }, timeoutMs)

    return () => window.clearTimeout(timeoutId)
  }, [
    activeAction,
    activeEntryPoint,
    onLifecycleError,
    onTrack,
    operation?.operationId,
    operation?.status,
    operationId,
    refreshAuthoritativeCardState,
  ])

  // Operation identity and status primitives keep terminal side effects from
  // re-running when a background refetch returns an equivalent operation.
  useEffect(() => {
    if (
      !operation ||
      !activeAction ||
      !isTerminalOperation(operation) ||
      handledTerminalOperationIdRef.current === operation.operationId
    ) {
      return
    }

    handledTerminalOperationIdRef.current = operation.operationId
    idempotencyRef.current = undefined
    setActiveAction(undefined)
    setActiveEntryPoint(undefined)
    refreshAuthoritativeCardState()
    if (
      activeAction === 'replace' ||
      activeAction === 'lostOrStolen' ||
      activeAction === 'cancel'
    ) {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const creditCardsIndex = query.queryKey.indexOf(CREDIT_CARDS_QK)
          const listFilters = query.queryKey[creditCardsIndex + 1]
          return (
            creditCardsIndex !== -1 &&
            typeof listFilters === 'object' &&
            listFilters !== null
          )
        },
      })
    }
    onTrack?.(
      `${analyticsPrefix(activeAction)}_${operation.status.toLowerCase()}`,
      activeEntryPoint,
    )
    onTerminal?.(activeAction, operation, isModalOpen)
  }, [
    operation?.operationId,
    operation?.status,
    activeAction,
    activeEntryPoint,
    isModalOpen,
    onTerminal,
    onTrack,
    queryClient,
    refreshAuthoritativeCardState,
  ])

  useEffect(() => {
    if (
      !operationId ||
      !operationQuery.error ||
      !activeAction ||
      handledOperationReadErrorIdRef.current === operationId
    ) {
      return
    }

    handledOperationReadErrorIdRef.current = operationId
    setErrorKind('uncertain')
    setOperationId(undefined)
    setActiveAction(undefined)
    setActiveEntryPoint(undefined)
    refreshAuthoritativeCardState()
    onTrack?.(
      `${analyticsPrefix(activeAction)}_operation_read_failed`,
      activeEntryPoint,
    )
    onLifecycleError?.(activeAction, 'uncertain', isModalOpenRef.current)
  }, [
    activeAction,
    activeEntryPoint,
    onLifecycleError,
    onTrack,
    operationId,
    operationQuery.error,
    refreshAuthoritativeCardState,
  ])

  const openAction = useCallback(
    (
      action: PhysicalCardLifecycleAction,
      entryPoint: PhysicalCardLifecycleEntryPoint,
    ): void => {
      if (!availability[action] || hasActiveCardOperation) {
        return
      }

      if (operation && isTerminalOperation(operation)) {
        setOperationId(undefined)
        setAcceptedTerminalOperation(undefined)
        setActiveAction(undefined)
      }
      setErrorKind(undefined)
      setSelection({ action, entryPoint })
      onTrack?.(`${analyticsPrefix(action)}_opened`, entryPoint)
    },
    [availability, hasActiveCardOperation, onTrack, operation],
  )

  const closeModal = useCallback((): void => {
    setSelection(undefined)
    setAcceptedTerminalOperation(undefined)
  }, [])

  const freezeAction = ((): 'freeze' | 'unfreeze' | undefined => {
    if (cardStatus === 'ACTIVE' && availability.freeze) return 'freeze'
    if (cardStatus === 'FROZEN' && availability.unfreeze) return 'unfreeze'
    return undefined
  })()
  const canToggleFreeze =
    !hasUnresolvedLifecycleOperation &&
    !isFreezePending &&
    freezeAction !== undefined

  const toggleFreeze = useCallback(
    async (entryPoint?: PhysicalCardLifecycleEntryPoint): Promise<void> => {
      if (
        !creditCardId ||
        expectedVersion === undefined ||
        !canToggleFreeze ||
        freezeSubmittingRef.current
      ) {
        return
      }

      freezeSubmittingRef.current = true
      setIsFreezePending(true)
      const statusAction = cardStatus === 'ACTIVE' ? 'freeze' : 'unfreeze'
      onTrack?.(`${statusAction}_confirmed`, entryPoint)
      const fingerprint = `${creditCardId}:${cardStatus}:${expectedVersion}`
      if (freezeIdempotencyRef.current?.fingerprint !== fingerprint) {
        freezeIdempotencyRef.current = {
          fingerprint,
          key: createPhysicalCardLifecycleIdempotencyKey(),
        }
      }
      const input = {
        creditCardId,
        request: { expectedVersion },
        idempotencyKey: freezeIdempotencyRef.current.key,
      }

      let operationPending = false
      try {
        const statusChange =
          cardStatus === 'ACTIVE'
            ? await freezeMutation.mutateAsync(input)
            : await unfreezeMutation.mutateAsync(input)
        operationPending = statusChange.status === 'PENDING'
        if (operationPending) {
          setPendingStatusChange({
            action: statusAction,
            entryPoint,
            expectedStatus: cardStatus === 'ACTIVE' ? 'FROZEN' : 'ACTIVE',
            startingVersion: expectedVersion,
          })
        }
        if (!operationPending) {
          freezeIdempotencyRef.current = undefined
          refreshAuthoritativeCardState()
          onTrack?.(`${statusAction}_succeeded`, entryPoint)
        }
      } catch (error) {
        const classified = classifyPhysicalCardLifecycleError(
          error as QueryError,
        )
        if (classified !== 'transport' && classified !== 'uncertain') {
          freezeIdempotencyRef.current = undefined
        }
        if (classified !== 'transport') {
          refreshAuthoritativeCardState()
        }
        onTrack?.(`${statusAction}_request_failed`, entryPoint)
        onFreezeError?.(classified)
      } finally {
        if (!operationPending) {
          freezeSubmittingRef.current = false
          setIsFreezePending(false)
        }
      }
    },
    [
      canToggleFreeze,
      cardStatus,
      creditCardId,
      expectedVersion,
      freezeMutation.mutateAsync,
      onFreezeError,
      onTrack,
      refreshAuthoritativeCardState,
      unfreezeMutation.mutateAsync,
    ],
  )

  const submit = useCallback(
    async (
      currentSelection: LifecycleSelection,
      idempotencyKey: string,
    ): Promise<CardManagementLifecycleOperationResponse> => {
      if (!creditCardId || expectedVersion === undefined) {
        throw {
          error: 'Card status is not available',
        } satisfies QueryError
      }

      const baseInput = {
        creditCardId,
        request: { expectedVersion },
        idempotencyKey,
      }
      switch (currentSelection.action) {
        case 'replace':
          return replacementMutation.mutateAsync({
            ...baseInput,
            request: { ...baseInput.request, reason: 'DAMAGED' },
          })
        case 'lostOrStolen':
          return replacementMutation.mutateAsync({
            ...baseInput,
            request: { ...baseInput.request, reason: 'LOST_OR_STOLEN' },
          })
        case 'reportFraud':
          return fraudMutation.mutateAsync(baseInput)
        case 'cancel':
          return cancellationMutation.mutateAsync(baseInput)
      }
    },
    [
      cancellationMutation.mutateAsync,
      creditCardId,
      expectedVersion,
      fraudMutation.mutateAsync,
      replacementMutation.mutateAsync,
    ],
  )

  const confirmAction = useCallback(async (): Promise<void> => {
    if (
      !selection ||
      !availability[selection.action] ||
      submittingRef.current ||
      !creditCardId ||
      expectedVersion === undefined
    ) {
      return
    }

    submittingRef.current = true
    setSubmitting(true)
    setErrorKind(undefined)
    setActiveAction(selection.action)
    setActiveEntryPoint(selection.entryPoint)
    onTrack?.(
      `${analyticsPrefix(selection.action)}_confirmed`,
      selection.entryPoint,
    )
    const fingerprint = `${creditCardId}:${selection.action}:${expectedVersion}`
    if (idempotencyRef.current?.fingerprint !== fingerprint) {
      idempotencyRef.current = {
        fingerprint,
        key: createPhysicalCardLifecycleIdempotencyKey(),
      }
    }

    try {
      const acceptedOperation = await submit(
        selection,
        idempotencyRef.current.key,
      )
      if (isTerminalOperation(acceptedOperation)) {
        setAcceptedTerminalOperation(acceptedOperation)
        setOperationId(undefined)
      } else {
        setAcceptedTerminalOperation(undefined)
        queryClient.setQueryData(
          physicalCardManagementQueryKeys.operation(
            acceptedOperation.operationId,
          ),
          acceptedOperation,
        )
        setOperationId(acceptedOperation.operationId)
      }
      onTrack?.(
        `${analyticsPrefix(selection.action)}_accepted`,
        selection.entryPoint,
      )
    } catch (error) {
      const classified = classifyPhysicalCardLifecycleError(error as QueryError)
      setErrorKind(classified)
      setActiveAction(undefined)
      setActiveEntryPoint(undefined)
      if (classified !== 'transport' && classified !== 'uncertain') {
        idempotencyRef.current = undefined
      }
      if (classified !== 'transport') {
        refreshAuthoritativeCardState()
      }
      onTrack?.(
        `${analyticsPrefix(selection.action)}_request_failed`,
        selection.entryPoint,
      )
    } finally {
      submittingRef.current = false
      setSubmitting(false)
    }
  }, [
    availability,
    creditCardId,
    expectedVersion,
    onTrack,
    queryClient,
    refreshAuthoritativeCardState,
    selection,
    submit,
  ])

  const phase = useMemo<PhysicalCardLifecyclePhase>(() => {
    if (submitting) return 'submitting'
    if (errorKind) return 'error'
    switch (operation?.status) {
      case 'SUCCEEDED':
        return 'succeeded'
      case 'FAILED':
        return 'failed'
      case 'CANCELED':
        return 'canceled'
      case 'PENDING_APPROVAL':
      case 'QUEUED':
      case 'PROCESSING':
        return 'processing'
      default:
        return 'confirmation'
    }
  }, [errorKind, operation?.status, submitting])

  const modalState = useMemo<PhysicalCardLifecycleModalState | undefined>(
    () =>
      selection
        ? {
            ...selection,
            phase,
            operation,
            errorKind,
          }
        : undefined,
    [errorKind, operation, phase, selection],
  )

  const isActionInProgress = useCallback(
    (_action: PhysicalCardLifecycleAction): boolean => hasActiveCardOperation,
    [hasActiveCardOperation],
  )

  return useMemo(
    () => ({
      availability,
      modalState,
      openAction,
      closeModal,
      confirmAction,
      canToggleFreeze,
      freezeAction,
      toggleFreeze,
      isFreezePending,
      isActionInProgress,
    }),
    [
      availability,
      canToggleFreeze,
      closeModal,
      confirmAction,
      isActionInProgress,
      isFreezePending,
      freezeAction,
      modalState,
      openAction,
      toggleFreeze,
    ],
  )
}
